/**
 * The model an offer is about.
 *
 * The offer DTO carries no model field - the offers API has one, but the site's DTO never mapped it - so this
 * has to be read out of the text an editor wrote. Two sources, in order of how reliable they are:
 *
 *  1. The category, when it takes the form "Subaru <Model> Offer". The live data uses that shape for every
 *     model category ("Subaru Outback Offer", "Subaru WRX Offer") and a different shape for categories that
 *     name a kind of offer rather than a car ("Special Offer", "Servicing Offer", "Retail Event").
 *  2. Failing that, a known model name appearing in the category or the offer's title. Most live offers sit
 *     in the general "Special Offer" category, so without this step the panel almost never names a model -
 *     which is what the redesign asks it to do.
 *
 * Step 2 needs the list below because there is nothing on the offer to identify the car: matching "the word
 * after Subaru" would read "Subaru Runout Sale" as a model called Runout. When Subaru adds a model, add it
 * here; until the offer DTO carries the model code, this is the only place that knowledge can live.
 *
 * An offer that is about no single model - servicing, accessories - yields null, and the caller words itself
 * generically rather than inventing a car.
 */
const MODEL_CATEGORY = /^subaru\s+(.+?)\s+offer$/i;

const KNOWN_MODELS = [
  'Outback',
  'Forester',
  'Crosstrek',
  'Impreza',
  'WRX',
  'BRZ',
  'Liberty',
  'Levorg',
  'Solterra',
  'Ascent',
  'XV',
] as const;

export function offerModelName(
  category?: string | null,
  title?: string | null
): string | null {
  const fromCategory = MODEL_CATEGORY.exec((category ?? '').trim());
  if (fromCategory) {
    const model = fromCategory[1].trim();
    if (model.length > 0) return model;
  }

  return findKnownModel(category) ?? findKnownModel(title);
}

function findKnownModel(text?: string | null): string | null {
  if (!text) return null;

  // Whole words only, so "Crosstrek" is not found inside a longer word and "XV" is not found in "XVI".
  return (
    KNOWN_MODELS.find((model) =>
      new RegExp(`\\b${model}\\b`, 'i').test(text)
    ) ?? null
  );
}
